import { buildChatEditor } from '@core/component/AI/component/input/buildChatEditor';
import { MarkdownShell } from '@core/component/LexicalMarkdown/builder/MarkdownShell';
import clickOutside from '@core/directive/clickOutside';
import { TOKENS } from '@core/hotkey/tokens';
import SparkleIcon from '@phosphor/sparkle.svg';
import {
  cancelAiEdit,
  requestAiEdit,
  toastAiEditResult,
} from '@service-ai-editing/client';
import { Button, ComposerSurface, SendButton } from '@ui';
import { createSignal, Show } from 'solid-js';
import { useMarkdownDocument } from '../context/markdown-document-context';

false && clickOutside;

/**
 * Right-aligned "Edit with AI" pill above the document discussion that expands
 * into a prompt card matching the Discussion composer.
 */
export function DocumentAiEditBar(props: { documentId: string }) {
  const { state } = useMarkdownDocument();

  const [expanded, setExpanded] = createSignal(false);
  const [editing, setEditing] = createSignal(false);
  const [hovering, setHovering] = createSignal(false);
  const [prompt, setPrompt] = createSignal('');

  let editor = buildChatEditor();

  const collapse = () => {
    setExpanded(false);
    setPrompt('');
    state.editor.md.editor?.focus();
  };

  const submit = () => {
    const value = prompt().trim();
    if (!value || editing()) return;
    editor.controls.clear();
    setPrompt('');
    collapse();
    setEditing(true);

    requestAiEdit({
      documentId: props.documentId,
      prompt: value,
    })
      .then(toastAiEditResult)
      .finally(() => setEditing(false));
  };

  const configureEditor = () =>
    buildChatEditor()
      .onEnter(() => {
        submit();
        return true;
      })
      .onEscape(() => {
        collapse();
        return true;
      })
      .onChange((markdown) => setPrompt(markdown));

  editor = configureEditor();

  const expand = () => {
    editor = configureEditor();
    setExpanded(true);
    requestAnimationFrame(() => editor.controls.focus());
  };

  return (
    <div class="flex justify-end">
      {/* breathe keyframe for the star while an edit runs */}
      <style>{`
        @keyframes ai-edit-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.18); }
        }
        .ai-edit-star-breathing {
          animation: ai-edit-breathe 1.8s ease-in-out infinite;
          transform-origin: center;
        }
      `}</style>
      <Show
        when={expanded()}
        fallback={
          <Button
            onClick={(event) => {
              event.stopPropagation();
              if (editing()) cancelAiEdit(props.documentId);
              else expand();
            }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            tooltip={editing() ? 'Stop AI edit' : 'Ask AI to edit'}
            hotkey={editing() ? undefined : TOKENS.chat.input.focus}
            variant="ghost"
            size="sm"
            depth={2}
            class="gap-1.5 rounded-full border border-edge-muted px-2.5"
          >
            <span
              class="flex size-4 shrink-0 items-center justify-center"
              classList={{
                'ai-edit-star-breathing text-accent': editing(),
              }}
            >
              <SparkleIcon />
            </span>
            <span
              class="text-xs font-medium"
              classList={{ 'text-ink-muted': editing() && !hovering() }}
            >
              {editing() ? (hovering() ? 'Stop' : 'Editing…') : 'Edit with AI'}
            </span>
          </Button>
        }
      >
        <ComposerSurface class="h-auto w-96 max-w-full">
          <div class="flex flex-col gap-2 p-2" use:clickOutside={collapse}>
            <div class="flex items-start gap-2">
              <span class="flex h-8 w-4 shrink-0 items-center justify-center text-accent">
                <SparkleIcon />
              </span>
              <div class="min-w-0 grow text-base text-ink">
                <MarkdownShell
                  config={editor}
                  placeholder="Describe the edit…"
                  autofocus
                />
              </div>
            </div>
            <div class="flex items-center justify-end">
              <SendButton
                tooltip="Send edit"
                shortcut="enter"
                disabled={prompt().trim().length === 0}
                onClick={submit}
              />
            </div>
          </div>
        </ComposerSurface>
      </Show>
    </div>
  );
}
